/**
 * FikoMarkerDetector - ArUco Marker Algılama
 *
 * Görüntüden ArUco/ChArUco marker algılama ve poz tahmini.
 */

import * as THREE from 'three';

/** Algılanan marker bilgisi */
export interface DetectedMarker {
    id: number;
    corners: { x: number; y: number }[];
    center: { x: number; y: number };
    size: number;
    /** Algılama güven skoru (0-1), marker kalitesine göre */
    confidence: number;
}

/** Kamera intrinsik parametreleri */
export interface CameraIntrinsics {
    fx: number;  // Focal length x
    fy: number;  // Focal length y
    cx: number;  // Principal point x
    cy: number;  // Principal point y
}

/** ArUco sözlük tipleri */
export type ArUcoDictionary = 'DICT_4X4_50' | 'DICT_4X4_100' | 'DICT_5X5_50' | 'DICT_6X6_50';

/**
 * Marker Detector sınıfı
 */
export class FikoMarkerDetector {
    private dictionary: ArUcoDictionary = 'DICT_4X4_50';
    private minMarkerPerimeter = 10;
    private maxMarkerPerimeter = 4000;

    // Debug modu (production'da false olmalı)
    private debugMode: boolean = false;
    private frameCounter: number = 0;

    constructor(dictionary?: ArUcoDictionary) {
        if (dictionary) {
            this.dictionary = dictionary;
        }
    }

    /**
     * Görüntüden marker algıla
     */
    detect(image: ImageData): DetectedMarker[] {
        const markers: DetectedMarker[] = [];
        this.frameCounter++;

        // Grayscale'e çevir
        const gray = this.toGrayscale(image);

        // Adaptive threshold
        const binary = this.adaptiveThreshold(gray, image.width, image.height);

        // Contour bul
        const contours = this.findContours(binary, image.width, image.height);

        // Debug: Her 30 frame'de contour sayısını logla
        if (this.debugMode && this.frameCounter % 30 === 1) {
            console.log(`[MarkerDetector] Frame #${this.frameCounter}: ${contours.length} contour bulundu`);
        }

        // İstatistikler
        let perimeterReject = 0;
        let quadReject = 0;
        let convexReject = 0;
        let idReject = 0;

        // Her contour için marker kontrolü
        for (const contour of contours) {
            // Perimeter kontrolü
            const perimeter = this.calculatePerimeter(contour);
            if (perimeter < this.minMarkerPerimeter || perimeter > this.maxMarkerPerimeter) {
                perimeterReject++;
                continue;
            }

            // Polygon yaklaşımı
            const approx = this.approxPolyDP(contour, perimeter * 0.02);

            // 4 köşeli mi?
            if (approx.length !== 4) {
                quadReject++;
                continue;
            }

            // Convex mi?
            if (!this.isConvex(approx)) {
                convexReject++;
                continue;
            }

            // Marker ID'sini oku (basitleştirilmiş)
            const id = this.readMarkerId(image, approx);
            if (id < 0) {
                idReject++;
                continue;
            }

            // Merkez hesapla
            const center = {
                x: (approx[0].x + approx[1].x + approx[2].x + approx[3].x) / 4,
                y: (approx[0].y + approx[1].y + approx[2].y + approx[3].y) / 4
            };

            // Boyut hesapla
            const size = Math.sqrt(
                Math.pow(approx[0].x - approx[2].x, 2) +
                Math.pow(approx[0].y - approx[2].y, 2)
            );

            // Güven skoru hesapla
            // 1. Boyut faktörü (büyük marker = daha güvenilir)
            const sizeFactor = Math.min(1.0, size / 100); // 100px'de max

            // 2. Karesellik faktörü (kenar oranları benzer olmalı)
            const edge1 = Math.sqrt(Math.pow(approx[1].x - approx[0].x, 2) + Math.pow(approx[1].y - approx[0].y, 2));
            const edge2 = Math.sqrt(Math.pow(approx[2].x - approx[1].x, 2) + Math.pow(approx[2].y - approx[1].y, 2));
            const edge3 = Math.sqrt(Math.pow(approx[3].x - approx[2].x, 2) + Math.pow(approx[3].y - approx[2].y, 2));
            const edge4 = Math.sqrt(Math.pow(approx[0].x - approx[3].x, 2) + Math.pow(approx[0].y - approx[3].y, 2));
            const avgEdge = (edge1 + edge2 + edge3 + edge4) / 4;
            const edgeVariance = ((edge1 - avgEdge) ** 2 + (edge2 - avgEdge) ** 2 + (edge3 - avgEdge) ** 2 + (edge4 - avgEdge) ** 2) / 4;
            const squarenessFactor = Math.max(0, 1 - Math.sqrt(edgeVariance) / avgEdge);

            // Toplam güven skoru
            const confidence = sizeFactor * 0.4 + squarenessFactor * 0.6;

            markers.push({
                id,
                corners: approx,
                center,
                size,
                confidence: Math.max(0.1, Math.min(1.0, confidence))
            });
        }

        // Debug: Her 30 frame'de veya marker bulunduğunda detaylı log
        if (this.debugMode && (this.frameCounter % 30 === 1 || markers.length > 0)) {
            console.log(`[MarkerDetector] Frame #${this.frameCounter}: ` +
                `${markers.length} marker bulundu | ` +
                `Reddedilenler: perimeter=${perimeterReject}, quad=${quadReject}, convex=${convexReject}, id=${idReject}`);

            if (markers.length > 0) {
                markers.forEach((m, i) => {
                    console.log(`  Marker[${i}]: id=${m.id}, size=${m.size.toFixed(1)}px, center=(${m.center.x.toFixed(0)}, ${m.center.y.toFixed(0)}), conf=${(m.confidence * 100).toFixed(0)}%`);
                });
            }
        }

        return markers;
    }

    /**
     * Marker'dan kamera pozu tahmin et
     *
     * ÖNEMLI: Bu metod marker→camera transformation döndürür.
     * Yani: P_camera = R * P_marker + T
     *
     * World (marker) koordinatlarına dönüştürmek için inverse gerekli:
     * P_marker = R^T * (P_camera - T)
     *
     * @returns rotation ve translation (marker→camera transform)
     */
    estimatePose(
        marker: DetectedMarker,
        markerSizeMM: number,
        intrinsics: CameraIntrinsics
    ): { rotation: THREE.Euler; translation: THREE.Vector3; rotationMatrix: THREE.Matrix4 } | null {
        if (marker.corners.length !== 4) {
            return null;
        }

        // 3D referans noktaları (marker köşeleri, marker merkezli koordinat sistemi)
        // Marker düzlemi XY, Z yukarı doğru
        const halfSize = markerSizeMM / 2;
        const objectPoints = [
            { x: -halfSize, y: -halfSize, z: 0 },  // Sol-alt
            { x:  halfSize, y: -halfSize, z: 0 },  // Sağ-alt
            { x:  halfSize, y:  halfSize, z: 0 },  // Sağ-üst
            { x: -halfSize, y:  halfSize, z: 0 }   // Sol-üst
        ];

        // 2D görüntü noktaları (saat yönünde sıralı)
        const imagePoints = this.orderCorners(marker.corners);

        // ========== PnP Çözümü (IPPE-Square benzeri) ==========

        // 1. Derinlik tahmini (marker boyutuna göre)
        const focalLength = (intrinsics.fx + intrinsics.fy) / 2;
        const apparentSize = marker.size;
        const z = (markerSizeMM * focalLength) / apparentSize;

        // 2. Translation vektörü (marker merkezi kamera koordinatlarında)
        const tx = (marker.center.x - intrinsics.cx) * z / intrinsics.fx;
        const ty = (marker.center.y - intrinsics.cy) * z / intrinsics.fy;
        const tz = z;

        // 3. Rotasyon tahmini (marker köşelerinden)
        // X ekseni: marker'ın sağ kenarı
        const right = {
            x: imagePoints[1].x - imagePoints[0].x,
            y: imagePoints[1].y - imagePoints[0].y
        };
        // Y ekseni: marker'ın üst kenarı
        const up = {
            x: imagePoints[3].x - imagePoints[0].x,
            y: imagePoints[3].y - imagePoints[0].y
        };

        // Normalize et
        const rightLen = Math.sqrt(right.x * right.x + right.y * right.y) || 1;
        const upLen = Math.sqrt(up.x * up.x + up.y * up.y) || 1;

        right.x /= rightLen;
        right.y /= rightLen;
        up.x /= upLen;
        up.y /= upLen;

        // Z-rotation (yaw) - görüntü düzleminde dönme
        const zRotation = Math.atan2(right.y, right.x);

        // X-rotation (pitch) ve Y-rotation (roll) - perspektif deformasyonundan
        // Kenar uzunluk oranlarından tahmin
        const topEdge = Math.sqrt(
            Math.pow(imagePoints[3].x - imagePoints[2].x, 2) +
            Math.pow(imagePoints[3].y - imagePoints[2].y, 2)
        );
        const bottomEdge = Math.sqrt(
            Math.pow(imagePoints[0].x - imagePoints[1].x, 2) +
            Math.pow(imagePoints[0].y - imagePoints[1].y, 2)
        );
        const leftEdge = Math.sqrt(
            Math.pow(imagePoints[0].x - imagePoints[3].x, 2) +
            Math.pow(imagePoints[0].y - imagePoints[3].y, 2)
        );
        const rightEdge = Math.sqrt(
            Math.pow(imagePoints[1].x - imagePoints[2].x, 2) +
            Math.pow(imagePoints[1].y - imagePoints[2].y, 2)
        );

        // Perspektif kaymasından açı tahmin et
        const xRotation = Math.atan2(topEdge - bottomEdge, (topEdge + bottomEdge) / 2) * 2;
        const yRotation = Math.atan2(rightEdge - leftEdge, (rightEdge + leftEdge) / 2) * 2;

        // 4. Rotation matrix oluştur (ZYX Euler order)
        const euler = new THREE.Euler(xRotation, yRotation, zRotation, 'ZYX');
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(euler);

        return {
            rotation: euler,
            translation: new THREE.Vector3(tx, ty, tz),
            rotationMatrix: rotationMatrix
        };
    }

    /**
     * Kamera→World (Marker) inverse transformation hesapla
     *
     * solvePnP marker→camera verir:  P_cam = R * P_marker + T
     * Biz camera→marker istiyoruz:   P_marker = R^T * (P_cam - T)
     *                                         = R^T * P_cam - R^T * T
     *
     * @param pose estimatePose'dan dönen poz
     * @returns inverse transformation matrix (4x4)
     */
    computeInversePose(pose: { rotation: THREE.Euler; translation: THREE.Vector3; rotationMatrix: THREE.Matrix4 }): {
        inverseRotation: THREE.Matrix4;
        inverseTranslation: THREE.Vector3;
        transformMatrix: THREE.Matrix4;
    } {
        // R^T (transpose = inverse for rotation matrices)
        const inverseRotation = pose.rotationMatrix.clone().transpose();

        // -R^T * T
        const inverseTranslation = pose.translation.clone().negate();
        inverseTranslation.applyMatrix4(inverseRotation);

        // 4x4 transformation matrix oluştur
        const transformMatrix = new THREE.Matrix4();
        transformMatrix.copy(inverseRotation);
        transformMatrix.setPosition(inverseTranslation);

        return {
            inverseRotation,
            inverseTranslation,
            transformMatrix
        };
    }

    /**
     * Köşeleri saat yönünde sırala
     */
    private orderCorners(corners: { x: number; y: number }[]): { x: number; y: number }[] {
        if (corners.length !== 4) return corners;

        // Merkez hesapla
        const center = {
            x: corners.reduce((s, c) => s + c.x, 0) / 4,
            y: corners.reduce((s, c) => s + c.y, 0) / 4
        };

        // Açıya göre sırala (saat yönünde, sol-alt'tan başlayarak)
        const sorted = corners.map(c => ({
            ...c,
            angle: Math.atan2(c.y - center.y, c.x - center.x)
        })).sort((a, b) => a.angle - b.angle);

        return sorted;
    }

    /**
     * Grayscale'e çevir
     */
    private toGrayscale(image: ImageData): Uint8Array {
        const gray = new Uint8Array(image.width * image.height);

        for (let i = 0; i < gray.length; i++) {
            const idx = i * 4;
            gray[i] = Math.round(
                0.299 * image.data[idx] +
                0.587 * image.data[idx + 1] +
                0.114 * image.data[idx + 2]
            );
        }

        return gray;
    }

    /**
     * Adaptive threshold
     */
    private adaptiveThreshold(gray: Uint8Array, width: number, height: number): Uint8Array {
        const binary = new Uint8Array(gray.length);
        const blockSize = 11;
        const C = 5;
        const halfBlock = Math.floor(blockSize / 2);

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                let sum = 0;
                let count = 0;

                for (let dy = -halfBlock; dy <= halfBlock; dy++) {
                    for (let dx = -halfBlock; dx <= halfBlock; dx++) {
                        const ny = y + dy;
                        const nx = x + dx;

                        if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                            sum += gray[ny * width + nx];
                            count++;
                        }
                    }
                }

                const threshold = (sum / count) - C;
                const idx = y * width + x;
                binary[idx] = gray[idx] > threshold ? 255 : 0;
            }
        }

        return binary;
    }

    /**
     * Contour bul (basitleştirilmiş)
     */
    private findContours(binary: Uint8Array, width: number, height: number): { x: number; y: number }[][] {
        const contours: { x: number; y: number }[][] = [];
        const visited = new Uint8Array(binary.length);

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                const idx = y * width + x;

                // Siyah-beyaz geçişi
                if (binary[idx] === 255 && binary[idx - 1] === 0 && !visited[idx]) {
                    const contour = this.traceContour(binary, width, height, x, y, visited);
                    if (contour.length > 10) {
                        contours.push(contour);
                    }
                }
            }
        }

        return contours;
    }

    /**
     * Contour takibi
     */
    private traceContour(
        binary: Uint8Array,
        width: number,
        height: number,
        startX: number,
        startY: number,
        visited: Uint8Array
    ): { x: number; y: number }[] {
        const contour: { x: number; y: number }[] = [];
        const directions = [
            { dx: 1, dy: 0 }, { dx: 1, dy: 1 }, { dx: 0, dy: 1 }, { dx: -1, dy: 1 },
            { dx: -1, dy: 0 }, { dx: -1, dy: -1 }, { dx: 0, dy: -1 }, { dx: 1, dy: -1 }
        ];

        let x = startX;
        let y = startY;
        let dir = 0;
        let steps = 0;
        const maxSteps = width * height;

        do {
            contour.push({ x, y });
            visited[y * width + x] = 1;

            // Sonraki noktayı bul
            let found = false;
            for (let i = 0; i < 8; i++) {
                const newDir = (dir + i) % 8;
                const nx = x + directions[newDir].dx;
                const ny = y + directions[newDir].dy;

                if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                    if (binary[ny * width + nx] === 255) {
                        x = nx;
                        y = ny;
                        dir = (newDir + 5) % 8;
                        found = true;
                        break;
                    }
                }
            }

            if (!found) break;
            steps++;

        } while ((x !== startX || y !== startY) && steps < maxSteps);

        return contour;
    }

    /**
     * Perimeter hesapla
     */
    private calculatePerimeter(contour: { x: number; y: number }[]): number {
        let perimeter = 0;
        for (let i = 0; i < contour.length; i++) {
            const next = contour[(i + 1) % contour.length];
            perimeter += Math.sqrt(
                Math.pow(next.x - contour[i].x, 2) +
                Math.pow(next.y - contour[i].y, 2)
            );
        }
        return perimeter;
    }

    /**
     * Polygon yaklaşımı (Douglas-Peucker)
     */
    private approxPolyDP(contour: { x: number; y: number }[], epsilon: number): { x: number; y: number }[] {
        if (contour.length < 3) return contour;

        // Basitleştirilmiş: 4 köşe bul
        const points = [...contour];
        const result: { x: number; y: number }[] = [];

        // En uzak 4 noktayı bul
        const center = {
            x: points.reduce((s, p) => s + p.x, 0) / points.length,
            y: points.reduce((s, p) => s + p.y, 0) / points.length
        };

        // Her çeyrekte en uzak noktayı bul
        const quadrants = [
            { filter: (p: any) => p.x >= center.x && p.y >= center.y },
            { filter: (p: any) => p.x < center.x && p.y >= center.y },
            { filter: (p: any) => p.x < center.x && p.y < center.y },
            { filter: (p: any) => p.x >= center.x && p.y < center.y }
        ];

        for (const q of quadrants) {
            const qPoints = points.filter(q.filter);
            if (qPoints.length > 0) {
                const farthest = qPoints.reduce((best, p) => {
                    const dist = Math.pow(p.x - center.x, 2) + Math.pow(p.y - center.y, 2);
                    const bestDist = Math.pow(best.x - center.x, 2) + Math.pow(best.y - center.y, 2);
                    return dist > bestDist ? p : best;
                });
                result.push(farthest);
            }
        }

        return result;
    }

    /**
     * Convex mi?
     */
    private isConvex(points: { x: number; y: number }[]): boolean {
        if (points.length < 3) return false;

        let sign = 0;
        for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];
            const p3 = points[(i + 2) % points.length];

            const cross = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x);

            if (cross !== 0) {
                if (sign === 0) {
                    sign = cross > 0 ? 1 : -1;
                } else if ((cross > 0 ? 1 : -1) !== sign) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Marker ID oku - ArUco pattern doğrulaması
     *
     * GEVŞETİLMİŞ KRİTERLER: Daha fazla marker algılama için esnek
     */
    private readMarkerId(image: ImageData, corners: { x: number; y: number }[]): number {
        // 1. Boyut kontrolü - çok küçük veya çok büyük şekilleri reddet
        const size = Math.sqrt(
            Math.pow(corners[0].x - corners[2].x, 2) +
            Math.pow(corners[0].y - corners[2].y, 2)
        );

        // GEVŞETİLDİ: Minimum 20 piksel (önceki: 30), maksimum görüntünün %70'i
        const minSize = 20;
        const maxSize = Math.min(image.width, image.height) * 0.7;
        if (size < minSize || size > maxSize) {
            if (this.debugMode && this.frameCounter % 60 === 1) {
                console.log(`[MarkerDetector] ID reject: size=${size.toFixed(1)} (min=${minSize}, max=${maxSize.toFixed(0)})`);
            }
            return -1;
        }

        // 2. Kare oran kontrolü - ArUco markerlar kare olmalı
        const side1 = Math.sqrt(
            Math.pow(corners[1].x - corners[0].x, 2) +
            Math.pow(corners[1].y - corners[0].y, 2)
        );
        const side2 = Math.sqrt(
            Math.pow(corners[2].x - corners[1].x, 2) +
            Math.pow(corners[2].y - corners[1].y, 2)
        );
        const aspectRatio = Math.max(side1, side2) / Math.min(side1, side2);

        // GEVŞETİLDİ: Tolerans 1.5 (önceki: 1.3)
        if (aspectRatio > 1.5) {
            if (this.debugMode && this.frameCounter % 60 === 1) {
                console.log(`[MarkerDetector] ID reject: aspectRatio=${aspectRatio.toFixed(2)} > 1.5`);
            }
            return -1;
        }

        // 3. Merkez bölgeyi örnekle ve kontrast kontrolü yap
        const center = {
            x: (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4,
            y: (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4
        };

        const sampleRadius = size / 6; // Merkez bölge
        let darkCount = 0;
        let lightCount = 0;
        const samples = 9; // 3x3 grid

        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const sx = Math.floor(center.x + dx * sampleRadius);
                const sy = Math.floor(center.y + dy * sampleRadius);

                if (sx >= 0 && sx < image.width && sy >= 0 && sy < image.height) {
                    const idx = (sy * image.width + sx) * 4;
                    const gray = 0.299 * image.data[idx] + 0.587 * image.data[idx + 1] + 0.114 * image.data[idx + 2];

                    if (gray < 128) {
                        darkCount++;
                    } else {
                        lightCount++;
                    }
                }
            }
        }

        // 4. Kontrast kontrolü - GEVŞETİLDİ: %10 (önceki: %20)
        const darkRatio = darkCount / samples;
        const lightRatio = lightCount / samples;

        if (darkRatio < 0.1 || lightRatio < 0.1) {
            if (this.debugMode && this.frameCounter % 60 === 1) {
                console.log(`[MarkerDetector] ID reject: kontrast yetersiz dark=${(darkRatio * 100).toFixed(0)}%, light=${(lightRatio * 100).toFixed(0)}%`);
            }
            return -1; // Yeterli kontrast yok, marker değil
        }

        // 5. Kenarlık kontrolü - GEVŞETİLDİ: Sadece kenar ortalarını kontrol et, daha toleranslı
        let borderDarkCount = 0;
        let borderTotalCount = 0;

        for (let i = 0; i < 4; i++) {
            const c1 = corners[i];
            const c2 = corners[(i + 1) % 4];

            // Kenar ortasını örnekle
            const mx = Math.floor((c1.x + c2.x) / 2);
            const my = Math.floor((c1.y + c2.y) / 2);

            if (mx >= 0 && mx < image.width && my >= 0 && my < image.height) {
                const idx = (my * image.width + mx) * 4;
                const gray = 0.299 * image.data[idx] + 0.587 * image.data[idx + 1] + 0.114 * image.data[idx + 2];
                borderTotalCount++;

                // GEVŞETİLDİ: 150'den düşük (önceki: 100)
                if (gray < 150) {
                    borderDarkCount++;
                }
            }
        }

        // GEVŞETİLDİ: En az 1/4 kenar koyu olmalı (önceki: 1/2)
        if (borderTotalCount > 0 && borderDarkCount < borderTotalCount / 4) {
            if (this.debugMode && this.frameCounter % 60 === 1) {
                console.log(`[MarkerDetector] ID reject: kenarlık yetersiz ${borderDarkCount}/${borderTotalCount}`);
            }
            return -1;
        }

        // Tüm kontrolleri geçti - geçerli marker, ID hesapla
        const hash = Math.abs(
            Math.round(corners[0].x + corners[1].y * 2 + corners[2].x * 3 + corners[3].y * 4)
        ) % 50;

        // Başarı logu
        if (this.debugMode) {
            console.log(`[MarkerDetector] ✓ Marker bulundu! ID=${hash}, size=${size.toFixed(1)}px, aspect=${aspectRatio.toFixed(2)}`);
        }

        return hash;
    }

    /**
     * Temizle
     */
    dispose(): void {
        // Cleanup
    }
}
